import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const emptyForm = {
  parent_partner_id: '',
  display_name: '',
  description: '',
  business_unit_code: '',
  contact_email: '',
  location: '',
};

function slugifyCode(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || '';
}

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function countLabel(count, singular, plural = `${singular}s`) {
  const value = Number(count || 0);
  return `${value} ${value === 1 ? singular : plural}`;
}

function BusinessUnitCard({ unit }) {
  const description = unit.description || unit.profile_description || 'No description provided.';
  const actionLabel = unit.your_access ? 'Open' : 'View';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 1,
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 2,
        },
      }}
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
              {unit.display_name || unit.business_unit_code || 'Business Unit'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          </Box>
          <Chip
            size="small"
            color={unit.your_access ? 'success' : 'default'}
            variant={unit.your_access ? 'filled' : 'outlined'}
            label={`Your Access: ${unit.your_access ? 'Yes' : 'No'}`}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={countLabel(unit.capability_count, 'Capability', 'Capabilities')} />
            <Chip size="small" variant="outlined" label={countLabel(unit.asset_count, 'Asset')} />
          </Stack>
          <Button
            component={RouterLink}
            to={`/business-units/${encodeURIComponent(unit.business_unit_id)}`}
            endIcon={<ArrowForwardRoundedIcon />}
            size="small"
          >
            {actionLabel}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function BusinessUnitsPage(props) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [codeTouched, setCodeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  async function loadBusinessUnits() {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/partners/user/business-units');
      const data = response.data || {};
      setState({ loading: false, error: '', data });
      const partners = Array.isArray(data.partners) ? data.partners : [];
      setForm((current) => ({
        ...current,
        parent_partner_id: current.parent_partner_id || (partners.length === 1 ? partners[0].partner_id : ''),
      }));
    } catch (error) {
      setState({
        loading: false,
        error: error?.response?.data?.message || error?.message || 'Unable to load business units.',
        data: null,
      });
    }
  }

  useEffect(() => {
    loadBusinessUnits();
  }, []);

  const partners = Array.isArray(state.data?.partners) ? state.data.partners : [];
  const units = Array.isArray(state.data?.business_units) ? state.data.business_units : [];
  const parentPartner = state.data?.partner || partners[0] || null;

  const filteredUnits = useMemo(() => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery) {
      return units;
    }
    return units.filter((unit) => normalize([
      unit.display_name,
      unit.business_unit_code,
      unit.description,
      unit.profile_description,
      unit.contact_email,
      unit.location,
    ].filter(Boolean).join(' ')).includes(normalizedQuery));
  }, [query, units]);

  const updateForm = (patch) => {
    setForm((current) => {
      const next = { ...current, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'display_name') && !codeTouched) {
        next.business_unit_code = slugifyCode(patch.display_name);
      }
      return next;
    });
  };

  const openCreateDialog = () => {
    const defaultPartnerId = partners.length === 1 ? partners[0].partner_id : '';
    setForm({ ...emptyForm, parent_partner_id: defaultPartnerId });
    setCodeTouched(false);
    setFormError('');
    setDialogOpen(true);
  };

  const closeCreateDialog = () => {
    if (saving) {
      return;
    }
    setDialogOpen(false);
    setFormError('');
  };

  const createBusinessUnit = async () => {
    if (!form.display_name.trim()) {
      setFormError('Business Unit Name is required.');
      return;
    }
    if (!form.business_unit_code.trim()) {
      setFormError('Business Unit Code is required.');
      return;
    }
    if (partners.length > 1 && !form.parent_partner_id) {
      setFormError('Partner is required.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const response = await axiosInstance.post('/partners/user/business-units', {
        parent_partner_id: form.parent_partner_id || undefined,
        display_name: form.display_name,
        description: form.description,
        business_unit_code: form.business_unit_code,
        contact_email: form.contact_email || null,
        location: form.location || null,
      });
      const createdUnit = response.data?.business_unit || {};
      setDialogOpen(false);
      await loadBusinessUnits();
      if (createdUnit.business_unit_id) {
        navigate(`/business-units/${encodeURIComponent(createdUnit.business_unit_id)}`);
      }
    } catch (error) {
      setFormError(error?.response?.data?.message || error?.message || 'Unable to create business unit.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Business Units' },
            ]}
          />

          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <BusinessRoundedIcon color="primary" />
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    Business Units
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {parentPartner?.display_name || parentPartner?.partner_code || 'Your organization'}
                </Typography>
              </Stack>
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateDialog}>
                Create Business Unit
              </Button>
            </Stack>

            <TextField
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search Business Units..."
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {state.loading ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography>Loading business units...</Typography>
              </Stack>
            ) : state.error ? (
              <Alert severity="error">{state.error}</Alert>
            ) : filteredUnits.length ? (
              <Stack spacing={2}>
                {filteredUnits.map((unit) => (
                  <BusinessUnitCard key={unit.business_unit_id} unit={unit} />
                ))}
              </Stack>
            ) : (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No business units match the current search.
                </Typography>
              </Paper>
            )}
          </Stack>
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={closeCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Create Business Unit</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {formError ? <Alert severity="error">{formError}</Alert> : null}

            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="overline" color="text.secondary">
                Partner
              </Typography>
              {partners.length > 1 ? (
                <TextField
                  select
                  value={form.parent_partner_id}
                  onChange={(event) => updateForm({ parent_partner_id: event.target.value })}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  required
                >
                  {partners.map((partner) => (
                    <MenuItem key={partner.partner_id} value={partner.partner_id}>
                      {partner.display_name || partner.partner_code}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {parentPartner?.display_name || parentPartner?.partner_code || 'Your Partner'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your Business Unit will be created under this Partner.
                  </Typography>
                </>
              )}
            </Paper>

            <TextField
              label="Business Unit Name"
              value={form.display_name}
              onChange={(event) => updateForm({ display_name: event.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(event) => updateForm({ description: event.target.value })}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Business Unit Code"
              value={form.business_unit_code}
              onChange={(event) => {
                setCodeTouched(true);
                updateForm({ business_unit_code: slugifyCode(event.target.value) });
              }}
              helperText="Auto-generated from the name. Can be changed."
              fullWidth
              required
            />
            <TextField
              label="Contact Email"
              value={form.contact_email}
              onChange={(event) => updateForm({ contact_email: event.target.value })}
              fullWidth
            />
            <TextField
              label="Location"
              value={form.location}
              onChange={(event) => updateForm({ location: event.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={createBusinessUnit} disabled={saving}>
            {saving ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
}
